import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { formatMoney, billingErrorKey } from "@/lib/billing";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { BillingCountrySelect } from "@/components/BillingCountrySelect";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Leaf, MapPin } from "lucide-react";
import { PLAN_FEATURES } from "@/lib/plans";

const TIER_UI = {
  free: { icon: Leaf, accent: "bg-secondary text-primary", feats: PLAN_FEATURES.free },
  plus: { icon: Sparkles, accent: "bg-chenia-rosesoft text-chenia-terracottadark", feats: PLAN_FEATURES.plus, popular: true },
  premium: { icon: Crown, accent: "bg-chenia-espresso text-chenia-cream", feats: PLAN_FEATURES.premium },
};

export default function Pricing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [searchParams] = useSearchParams();
  const requestedTier = searchParams.get("plan");
  const highlightedTier = requestedTier === "plus" || requestedTier === "premium" ? requestedTier : null;
  const [interval, setBillingInterval] = useState("month");
  const { data } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });

  const current = user?.plan_tier || "free";
  const storeManaged = data?.plan_source === "store";
  const annualEnabled = !!data?.annual_enabled && (data?.annual_plans || []).length > 0;
  const annual = annualEnabled && interval === "year";
  const countryKnown = !!data?.country;
  // Mensuel : tableau `plans` (inchangé) ; annuel : formule gratuite + `annual_plans`
  const shownPlans = annual ? [(data?.plans || [])[0], ...(data?.annual_plans || [])].filter(Boolean) : (data?.plans || []);
  const anyFallback = shownPlans.some((p) => p.tier !== "free" && p.available && p.fallback);

  useEffect(() => {
    if (!data || !highlightedTier) return;
    document.getElementById(`plan-card-${highlightedTier}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data, highlightedTier]);

  const checkout = async (tier) => {
    setBusy(tier);
    try {
      const { data: res } = await api.post("/payments/checkout", { tier, interval: annual ? "year" : "month", origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      const key = billingErrorKey(e.response?.data?.detail);
      toast.error(key ? t(key) : (e.response?.data?.detail || "Error"));
      setBusy("");
    }
  };

  return (
    <AppShell>
      <div className="text-center chenia-fade-up">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("pricing_title")}</h1>
        <p className="text-muted-foreground mt-2 max-w-lg mx-auto">{t("pricing_sub")}</p>
      </div>

      {data && (
        <div className="chenia-card p-4 mt-8 max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3" data-testid="billing-country-card">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-secondary text-primary shrink-0"><MapPin className="h-4 w-4" /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{t("billing_country")}</p>
            <p className="text-xs text-muted-foreground" data-testid="billing-currency-note">
              {countryKnown ? (anyFallback ? t("billing_fallback_chf") : t("billing_prices_in", { currency: data.currency.toUpperCase() })) : t("billing_choose_country")}
            </p>
          </div>
          <BillingCountrySelect value={data.country} countries={data.countries} source={data.country_source} />
        </div>
      )}

      {storeManaged && (
        <p className="chenia-card p-4 mt-4 max-w-5xl mx-auto text-sm" data-testid="plan-store-managed">{t("billing_store_managed")}</p>
      )}

      {annualEnabled && <div className="flex justify-center mt-6" data-testid="billing-interval-toggle">
        <div className="inline-flex rounded-full bg-secondary p-1">
          {["month", "year"].map((iv) => (
            <button key={iv} type="button" data-testid={`billing-interval-${iv}`} onClick={() => setBillingInterval(iv)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium ${interval === iv ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {t(iv === "year" ? "billing_annual" : "billing_monthly")}
              {iv === "year" && <span className="ml-1.5 text-[11px] opacity-80">· {t("billing_two_months")}</span>}
            </button>
          ))}
        </div>
      </div>}

      <div className="grid md:grid-cols-3 gap-5 mt-6 max-w-5xl mx-auto">
        {shownPlans.map((plan) => {
          const ui = TIER_UI[plan.tier];
          const Icon = ui.icon;
          const isCurrent = current === plan.tier;
          const paid = plan.tier !== "free";
          const canBuy = paid && countryKnown && plan.available && !isCurrent && !storeManaged;
          return (
            <div key={plan.tier} id={`plan-card-${plan.tier}`} data-testid={`plan-card-${plan.tier}`}
              className={`chenia-card p-7 relative flex flex-col ${ui.popular || highlightedTier === plan.tier ? "ring-2 ring-primary" : ""}`}>
              {ui.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold">{t("popular")}</span>}
              <span className={`grid place-items-center h-12 w-12 rounded-2xl ${ui.accent}`}><Icon className="h-6 w-6" /></span>
              <h3 className="mt-4 font-serif text-2xl font-semibold">{t(`plan_${plan.tier}`)}</h3>
              <p className="mt-1">
                <span className="font-serif text-4xl font-semibold" data-testid={`plan-price-${plan.tier}`}>
                  {!paid ? t("plan_free") : (countryKnown && plan.available ? formatMoney(plan.amount, plan.currency, lang) : "—")}
                </span>
                {paid && countryKnown && plan.available && <span className="text-muted-foreground text-sm"> {t(annual ? "billing_per_year" : "per_month")}</span>}
              </p>
              {paid && countryKnown && plan.available && plan.fallback && (
                <p className="mt-1 text-[11px] text-chenia-terracottadark" data-testid={`plan-fallback-${plan.tier}`}>{t("billing_fallback_chf")}</p>
              )}
              {paid && countryKnown && !plan.available && (
                <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`plan-unavailable-${plan.tier}`}>{t("billing_unavailable")}</p>
              )}
              <ul className="mt-5 space-y-2.5 flex-1">
                {ui.feats.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-chenia-sage shrink-0 mt-0.5" /> <span>{t(f)}</span>
                  </li>
                ))}
              </ul>
              {canBuy && (
                <p className="mt-4 text-[11px] text-muted-foreground" data-testid={`plan-charged-${plan.tier}`}>
                  {t(annual ? "billing_charged_year" : "billing_charged", { amount: formatMoney(plan.amount, plan.currency, lang), currency: plan.currency.toUpperCase() })}
                  {" "}{t("billing_autorenew")}
                </p>
              )}
              <Button
                data-testid={`plan-cta-${plan.tier}`}
                disabled={isCurrent || !paid || !canBuy || busy === plan.tier}
                onClick={() => canBuy && checkout(plan.tier)}
                className={`mt-3 rounded-full ${isCurrent ? "" : "bg-primary hover:bg-chenia-terracottadark"}`}
                variant={isCurrent ? "outline" : "default"}>
                {isCurrent ? t("current_plan") : (busy === plan.tier ? "…" : t("upgrade"))}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-muted-foreground text-center" data-testid="pricing-disclaimer">{t("disclaimer")}</p>
    </AppShell>
  );
}
